import { PIECE_STATUS, PIECE_STATUS_CODE, type BlindKingsGame, type GamePiece } from '@blind-kings/game-core';
import type { TeamPalette } from '../../player-colors';
import Piece from './Piece';

type PieceStatusEntry = {
  piece: GamePiece;
  pieceStatus: string;
};

const mapPiece = (piece: GamePiece, pieceStatus: string): PieceStatusEntry => ({
  piece,
  pieceStatus,
});

const sortPieces = (
  { piece: { value: a }, pieceStatus: c }: PieceStatusEntry,
  { piece: { value: b }, pieceStatus: d }: PieceStatusEntry
) => {
  if (a === b) {
    return Number(PIECE_STATUS_CODE[c]) - Number(PIECE_STATUS_CODE[d]);
  }
  return a - b;
};

export type PieceInfoProps = {
  playerIndex: number;
  blindKingsGame: BlindKingsGame;
  renderVertically?: boolean;
  teamPalettes?: Partial<Record<0 | 1, TeamPalette>>;
};

export default function PieceInfo({
  playerIndex,
  blindKingsGame,
  renderVertically,
  teamPalettes,
}: PieceInfoProps) {
  const allPlayerPotentialUnturnedPieces = blindKingsGame.getPlayerUnturnedPieces(playerIndex);
  const allPlayerGraveyardPieces = blindKingsGame.getPlayerGraveyard(playerIndex);
  const allPlayerInGamePieces = blindKingsGame.getPlayerActivePieces(playerIndex);

  const playerPieceStatus = [
    ...allPlayerInGamePieces.map((piece) => mapPiece(piece, PIECE_STATUS.ACTIVE)),
    ...allPlayerPotentialUnturnedPieces.map((piece) => mapPiece(piece, PIECE_STATUS.INACTIVE)),
    ...allPlayerGraveyardPieces.map((piece) => mapPiece(piece, PIECE_STATUS.DEAD)),
  ].sort(sortPieces);

  const railBg =
    teamPalettes?.[playerIndex as 0 | 1]?.fill ??
    (playerIndex === 0 ? '#6b7280' : '#ef4444');

  return (
    <div
      className={`flex min-h-0 min-w-0 divide-black border-black ${
        renderVertically
          ? 'h-full w-full flex-col divide-y border-y-0 border-x'
          : 'h-full w-full flex-row divide-x border-x-0 border-y'
      }`}
    >
      {playerPieceStatus.map(({ piece, pieceStatus }, i) => (
        <div
          key={i}
          className="relative min-h-0 min-w-0 flex-1 transition-colors"
          style={{
            backgroundColor:
              pieceStatus === PIECE_STATUS.ACTIVE
                ? `${railBg}66`
                : pieceStatus === PIECE_STATUS.DEAD
                  ? '#00000040'
                  : `${railBg}24`,
          }}
        >
          <div className="absolute inset-0 p-[6%]">
            <Piece
              piece={piece}
              pieceStatus={pieceStatus}
              teamPalettes={teamPalettes}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
